import { useState } from"react";
import { formatEUR, PCG_INDEX } from"@/lib/pcgUtils";
import { CheckCircle2, AlertCircle, Plus, Trash2 } from"lucide-react";

const EMPTY = () => ({ c:"", l:"", d:"", k:"" });

export default function Simulator() {
 const [titre, setTitre] = useState("");
 const [rows, setRows] = useState([EMPTY(), EMPTY()]);

 const totalD = rows.reduce((s, r) => s + (Number(r.d) || 0), 0);
 const totalK = rows.reduce((s, r) => s + (Number(r.k) || 0), 0);
 const ecart = totalD - totalK;
 const balanced = Math.abs(ecart) < 0.01 && totalD > 0;

 const updateRow = (i, field, val) => {
 setRows((cur) =>
 cur.map((r, ri) => {
 if (ri !== i) return r;
 const next = { ...r, [field]: val };
 if (field ==="c" && PCG_INDEX[val.trim()] && !r.l) {
 next.l = PCG_INDEX[val.trim()].lib;
 }
 if (field ==="d" && val && next.k) next.k ="";
 if (field ==="k" && val && next.d) next.d ="";
 return next;
 })
 );
 };

 const addRow = () => setRows((cur) => [...cur, EMPTY()]);
 const removeRow = (i) =>
 setRows((cur) => (cur.length > 2 ? cur.filter((_, ri) => ri !== i) : cur));
 const reset = () => {
 setTitre("");
 setRows([EMPTY(), EMPTY()]);
 };

 const warnings = [];
 rows.forEach((r, i) => {
 const num = (r.c ||"").trim();
 if (!num) return;
 const acc = PCG_INDEX[num];
 if (!acc) {
 warnings.push(`Ligne ${i + 1} : le compte ${num} n'est pas dans le PCG chargé.`);
 return;
 }
 const dVal = Number(r.d) || 0;
 const kVal = Number(r.k) || 0;
 if (acc.classe === 6 && kVal > 0 && dVal === 0) {
 warnings.push(
 `Ligne ${i + 1} : ${num} (${acc.lib}) est une charge — normalement au débit, pas au crédit (sauf extourne).`
 );
 }
 if (acc.classe === 7 && dVal > 0 && kVal === 0) {
 warnings.push(
 `Ligne ${i + 1} : ${num} (${acc.lib}) est un produit — normalement au crédit, pas au débit (sauf extourne).`
 );
 }
 });

 return (
 <div data-testid="simulator-view" className="space-y-6">
 <div>
 <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-[#15294F] tracking-tight">
 Passer une écriture
 </h2>
 <p className="text-slate-600 text-sm mt-1">
 Un journal libre pour vous entraîner. L'équilibre débit/crédit est vérifié en temps réel.
 </p>
 </div>

 <div className="bg-white border border-keyto rounded-xl shadow-keyto p-6 md:p-8">
 <input
 data-testid="sim-titre"
 value={titre}
 onChange={(e) => setTitre(e.target.value)}
 placeholder="Intitulé de l'opération (ex: Achat de fournitures)"
 className="w-full bg-slate-50 border border-keyto rounded-md px-4 py-3 text-base font-medium mb-5 focus:outline-none focus:border-[#2D6BE0] focus:ring-1 focus:ring-[#2D6BE0]"
 />

 <div className="bg-slate-50 border border-keyto rounded-lg overflow-hidden">
 <div className="grid grid-cols-12 bg-[#15294F] text-white text-xs font-semibold p-3 uppercase tracking-wider">
 <div className="col-span-2">Compte</div>
 <div className="col-span-5">Libellé</div>
 <div className="col-span-2 text-right">Débit</div>
 <div className="col-span-2 text-right">Crédit</div>
 <div className="col-span-1"></div>
 </div>
 {rows.map((r, i) => {
 const acc = PCG_INDEX[(r.c ||"").trim()];
 return (
 <div
 key={i}
 className="grid grid-cols-12 p-2 border-b border-slate-200 last:border-b-0 items-center gap-2 bg-white"
 >
 <input
 data-testid={`sim-row-${i}-compte`}
 value={r.c}
 onChange={(e) => updateRow(i,"c", e.target.value)}
 className={`col-span-2 font-mono font-bold px-2 py-1.5 border rounded-md bg-white text-sm focus:outline-none focus:ring-1 ${
 r.c && !acc ?"border-rose-500 focus:ring-rose-400" :"border-keyto focus:border-[#2D6BE0] focus:ring-[#2D6BE0]"
 }`}
 placeholder="401"
 />
 <input
 data-testid={`sim-row-${i}-libelle`}
 value={r.l}
 onChange={(e) => updateRow(i,"l", e.target.value)}
 className="col-span-5 px-2 py-1.5 border border-keyto rounded-md bg-white text-sm focus:outline-none focus:border-[#2D6BE0] focus:ring-1 focus:ring-[#2D6BE0]"
 placeholder="Libellé"
 />
 <input
 data-testid={`sim-row-${i}-debit`}
 type="number"
 step="0.01"
 value={r.d}
 onChange={(e) => updateRow(i,"d", e.target.value)}
 className="col-span-2 px-2 py-1.5 border border-keyto rounded-md bg-white text-sm text-right font-mono focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
 placeholder="0"
 />
 <input
 data-testid={`sim-row-${i}-credit`}
 type="number"
 step="0.01"
 value={r.k}
 onChange={(e) => updateRow(i,"k", e.target.value)}
 className="col-span-2 px-2 py-1.5 border border-keyto rounded-md bg-white text-sm text-right font-mono focus:outline-none focus:border-rose-500 focus:ring-1 focus:ring-rose-500"
 placeholder="0"
 />
 <button
 data-testid={`sim-row-${i}-remove`}
 onClick={() => removeRow(i)}
 disabled={rows.length <= 2}
 className="col-span-1 text-slate-400 hover:text-rose-600 disabled:opacity-30 flex items-center justify-center"
 >
 <Trash2 size={14} />
 </button>
 </div>
 );
 })}
 <div className="grid grid-cols-12 p-3 bg-slate-100 border-t border-slate-200 font-mono text-sm font-bold">
 <div className="col-span-7 uppercase tracking-wider text-xs flex items-center gap-2 text-slate-600">
 {balanced && <CheckCircle2 size={14} className="text-emerald-600" />}
 {balanced ?"Équilibré" : `Écart : ${formatEUR(Math.abs(ecart))}`}
 </div>
 <div className="col-span-2 text-right text-[#15294F]">{formatEUR(totalD)}</div>
 <div className="col-span-2 text-right text-[#15294F]">{formatEUR(totalK)}</div>
 <div className="col-span-1"></div>
 </div>
 </div>

 <div className="flex flex-wrap gap-3 mt-4">
 <button
 data-testid="sim-add-row"
 onClick={addRow}
 className="bg-white text-[#15294F] font-semibold py-2.5 px-4 rounded-md border border-keyto shadow-keyto-sm hover:bg-slate-50 transition-all text-sm flex items-center gap-2"
 >
 <Plus size={14} /> Ajouter une ligne
 </button>
 <button
 data-testid="sim-reset"
 onClick={reset}
 className="bg-white text-[#15294F] font-semibold py-2.5 px-4 rounded-md border border-keyto shadow-keyto-sm hover:bg-slate-50 transition-all text-sm"
 >
 Réinitialiser
 </button>
 </div>

 {warnings.length > 0 && (
 <div
 data-testid="sim-warnings"
 className="mt-4 p-4 rounded-lg border border-amber-300 bg-amber-50"
 >
 <div className="flex items-center gap-2 mb-2">
 <AlertCircle size={16} className="text-amber-700" />
 <p className="font-semibold text-amber-900 text-sm">Vérifications de cohérence</p>
 </div>
 <ul className="space-y-1">
 {warnings.map((w, i) => (
 <li key={i} className="text-xs text-amber-900 leading-relaxed">
 • {w}
 </li>
 ))}
 </ul>
 </div>
 )}

 {balanced && warnings.length === 0 && (
 <div className="mt-4 p-4 rounded-lg border border-emerald-200 bg-emerald-50 flex items-center gap-3">
 <CheckCircle2 size={18} className="text-emerald-700 shrink-0" />
 <p className="text-sm text-emerald-900">
 Votre écriture est équilibrée et cohérente avec le PCG. Bonne pratique !
 </p>
 </div>
 )}
 </div>
 </div>
 );
}
